import axios from "axios";
import { Form } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import BgLogin from "../../assets/images/Bglogin.png";
import DefaultLogo from "../../assets/svg/default-logo.svg";
import CommonComponents from "../../components/Commons";
import { getStaticConfigs } from "../../helpers/common";
import { showError } from "../../helpers/toast";

interface ILoginForm
{
  accessKey: string;
}

export default function LoginPage()
{
  const { t } = useTranslation();
  const [form] = Form.useForm<ILoginForm>();
  const [loading, setLoading] = useState(false);

  const handleLogin = async (values: ILoginForm) =>
  {
    setLoading(true);
    try
    {
      const config: any = await getStaticConfigs();

      // OAuth2 token exchange: the access key entered here is the `basic_key`,
      // reused later by the token-refresh interceptor (stored as "key").
      const key = values.accessKey?.trim();

      const axiosNoInterceptor = axios.create();
      const response = await axiosNoInterceptor.post(
        config?.REACT_APP_KEY_URL_OAUTH2,
        { basic_key: key },
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      const token = response?.data?.access_token;
      const tokenType = response?.data?.token_type || "Bearer";

      if (!token)
      {
        showError(t("Login failed. Please check your credentials."));
        setLoading(false);
        return;


      }

      localStorage.setItem("token", token);
      localStorage.setItem("token_type", tokenType);
      localStorage.setItem("key", key);

      // Client-side transition (no full reload → no blank/white flash). App
      // listens for this event, shows CustomLoading, loads all global data,
      // then navigates to "/". A single, consistent loader is shown.
      window.dispatchEvent(new Event("app:login"));
    } catch (error)
    {
      console.error(error);
      showError(t("Login failed. Please check your credentials."));
      setLoading(false);
    }
  };

  return (
    <div
      className="w-screen h-screen flex items-center justify-end bg-cover bg-center"
      style={{ backgroundImage: `url(${BgLogin})` }}
    >
      <div className="w-1/3 min-w-[420px] h-full flex items-center justify-center px-12 bg-black/40 backdrop-blur-md">
        <div className="w-full max-w-[420px]">
          <div className="flex flex-col items-center mb-8">
            <img src={DefaultLogo} alt="logo" className="h-12 mb-4" />
            <h1 className="text-2xl font-semibold text-white">
              {t("Sign in")}
            </h1>
            <p className="text-sm text-white/60 mt-1 text-center">
              {t("Sign in to continue to the platform")}
            </p>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleLogin}
            requiredMark={false}
          >
            <Form.Item
              name="accessKey"
              rules={[
                { required: true, message: t("Please enter your access key") },
              ]}
            >
              <CommonComponents.TextAreaUI
                label={t("Access key")}
                required
                autoSize={{ minRows: 4, maxRows: 8 }}
                placeholder={t("Enter your access key")}
                autoComplete="off"
                onPressEnter={(e: any) =>
                {
                  // Enter submits ``instead of adding a newlne (Shift+Enter = newline).
                  if (!e.shiftKey)
                  {
                    e.preventDefault();
                    form.submit();
                  }
                }}
              />
            </Form.Item>

            <CommonComponents.ButtonUI
              htmlType="submit"
              loading={loading}
              className="w-full !h-[40px] mt-2 !border-0 !text-black"
              style={{ backgroundColor: "var(--color-bg-btn-nav-primary)" }}
            >
              {t("Sign in")}
            </CommonComponents.ButtonUI>
          </Form>
        </div>
      </div>
    </div>
  );
}
